import { AxiosResponse } from 'axios';
import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { RootState } from '.';
import { Config } from '../helpers/types';
import { CheckProcessingResponse, Job } from '../../../shared/types/job';
import ApiService from '../services/ApiService';
import { ManualCombinationResult } from '@/helpers/types/manualCombination';

export type State = {
  cancelOrdersProcessing: boolean;
  config?: Config;
  isLoading: boolean;
  ordersProcessing: CheckProcessingResponse;
  manualCombinationList: Array<ManualCombinationResult>;
  version?: { commitHash: string; buildVersion: string };
  filters: any; // TODO: find type for filters
  constants: Record<string, any>;
  enums: Record<string, any>;
  job?: Job;
  checkOrdersProcessing: {
    interval?: NodeJS.Timeout;
    timeoutMs?: number;
  };
  finishedJobIds: string[];
};

const state: State = {
  cancelOrdersProcessing: false,
  config: undefined,
  isLoading: false,
  ordersProcessing: {
    isAutoProcessing: false,
    isLastManual: false,
    isProcessing: false,
    hasUserUnfinishedJobs: false,
    jobsInFrontOfThisOne: undefined,
    lastSmsResultCode: 0,
    newOrdersCount: 0,
    newPartialOrdersCount: 0
  },
  manualCombinationList: [],
  version: undefined,
  filters: {},
  constants: {},
  enums: {},
  job: undefined,
  checkOrdersProcessing: {
    interval: undefined,
    timeoutMs: 5000
  },
  finishedJobIds: []
};

const actions: ActionTree<State, RootState> = {
  async fetchVersion({ commit }) {
    const { data } = await ApiService.getVersion();
    commit('setVersion', data.version);
  },

  async fetchConfig({ commit, dispatch }) {
    const { data } = await ApiService.getConfig();
    const { config, constants, enums } = data;
    commit('setConfig', { config, constants, enums });
    await dispatch('basicSettings/get', null, { root: true });
    if (config.disableAuth) {
      commit('account/disableAuth', null, { root: true });
    }
  },

  async fetchOrdersProcessing({ commit, dispatch, rootState, state }) {
    const { data } = (await ApiService.checkOrdersProcessing(
      state.job?.jobId
    )) as AxiosResponse<CheckProcessingResponse>;
    if (!data.hasUserUnfinishedJobs) {
      dispatch('unsetJob');
    }
    if (state.checkOrdersProcessing.interval) {
      if (data.isProcessing && state.checkOrdersProcessing.timeoutMs !== 1000) {
        dispatch('startFetchOrdersProcessing', 1000);
      } else if (!data.isProcessing && state.checkOrdersProcessing.timeoutMs !== 5000) {
        dispatch('startFetchOrdersProcessing', 5000);
      }
    }

    const processingResults = data.processingResults ?? [];
    const processingJobIds = processingResults.map(({ jobId }) => jobId);

    // clean up finished jobs that are no longer in processing results because they were deleted
    for (const finishedJobId of state.finishedJobIds) {
      if (!processingJobIds.includes(finishedJobId)) {
        commit('removeManualCombinationByJobId', finishedJobId);
      }
    }

    // process new finished jobs
    const newFinishedJobs = processingResults.filter(
      ({ jobId }) => !state.finishedJobIds.includes(jobId)
    );

    for (const result of newFinishedJobs) {
      if (result.isPrePlan) {
        dispatch('ordersMapPlanner/processPlanningResult', result, { root: true });
        dispatch('unsetJob');
      } else if (!data.isAutoProcessing) {
        if (result.isMapPlannerJob) {
          dispatch('ordersMapPlanner/processPlanningResult', result, { root: true });
        }
        // current status: does-not-fit
        else if (result.status === rootState.app.enums.ProcessingStatus.DOES_NOT_FIT) {
          commit('addManualCombination', {
            freightIds: undefined,
            jobId: result.jobId,
            message: 'Sikertelen tervezés:\n' + 'A kijelölt törtfuvarok nem férnek fel egy járműre.'
          });
        }
        // current status: noOverlappingDeliveryDates
        else if (
          result.status === rootState.app.enums.ProcessingStatus.NO_OVERLAPPING_DELIVERY_DATES
        ) {
          commit('addManualCombination', {
            freightIds: undefined,
            jobId: result.jobId,
            message:
              'Sikertelen tervezés:\n' +
              'A kijelölt fuvarok szállítási intervallumai nem fedik egymást.'
          });
        }
        // current status: tooManyWaypoints
        else if (result.status === rootState.app.enums.ProcessingStatus.TOO_MANY_WAYPOINTS) {
          commit('addManualCombination', {
            freightIds: undefined,
            jobId: result.jobId,
            message:
              'Sikertelen tervezés:\n' +
              'A fuvarokon túl sok címpont található.\n' +
              'Legfeljebb 9 egyedi címpont fűzhető fel egy fuvarra.'
          });
        }
        // current status: noRouteFound
        else if (result.status === rootState.app.enums.ProcessingStatus.ROUTE_NOT_FOUND) {
          commit('addManualCombination', {
            freightIds: undefined,
            jobId: result.jobId,
            message: 'Sikertelen tervezés:\n' + 'Nem sikerült útvonalat találni a címpontokhoz.'
          });
        } else if (result.status === rootState.app.enums.ProcessingStatus.NO_VEHICLE) {
          commit('addManualCombination', {
            freightIds: undefined,
            jobId: result.jobId,
            message:
              'Sikertelen tervezés:\n' +
              'A megadott járműve(kke)l nem lehet teljesíteni a rendelést!'
          });
        } else if (result.status === rootState.app.enums.ProcessingStatus.ORDER_COVERAGE_ERROR) {
          commit('addManualCombination', {
            freightIds: undefined,
            jobId: result.jobId,
            message:
              'Sikertelen tervezés:\n' +
              'A létrejövő fuvarok nem fednék tökéletesen a feldolgozott rendeléseket.'
          });
        } else if (result.status === rootState.app.enums.ProcessingStatus.ERROR) {
          commit('addManualCombination', {
            freightIds: undefined,
            jobId: result.jobId,
            message: 'Sikertelen tervezés:\n Hiba történt a tervezés során.'
          });
        } else if (result.status === rootState.app.enums.ProcessingStatus.DONE) {
          if (result.resultIds && result.resultIds.length > 0) {
            commit('addManualCombination', {
              freightIds: result.resultIds,
              jobId: result.jobId,
              message:
                result.resultIds.length > 1
                  ? 'A manuális tervezés eredményeként a következő fuvarok jöttek létre: ' +
                    result.resultIds.join(', ')
                  : 'A manuális tervezés eredményeként a következő fuvar jött létre: ' +
                    result.resultIds[0]
            });
          }
        }
      }
      state.finishedJobIds.push(result.jobId);
    }

    commit('setOrdersProcessing', data);
  },

  // TODO solve to show multiple result cards if multiple jobs finished
  async deleteJobById({ state }, jobId: string) {
    const { status } = await ApiService.deleteJobById(jobId);
    if (status === 200) {
      state.finishedJobIds = state.finishedJobIds.filter((id) => id !== jobId);
    }
  },

  startFetchOrdersProcessing({ state, dispatch, commit }, timeoutMs: number) {
    if (state.checkOrdersProcessing.interval) {
      clearInterval(state.checkOrdersProcessing.interval);
    }
    const intervalId = setInterval(() => {
      try {
        dispatch('fetchOrdersProcessing');
      } catch {
        // no-op
      }
    }, timeoutMs);
    commit('setFetchOrdersProcessingState', { intervalId, timeoutMs });
  },

  stopFetchOrdersProcessing({ state, commit }) {
    if (state.checkOrdersProcessing.interval) {
      clearInterval(state.checkOrdersProcessing.interval);
      commit('setFetchOrdersProcessingState', { intervalId: undefined, timeoutMs: undefined });
    }
  },

  setLoading({ commit }, isLoading) {
    commit('setLoading', isLoading);
  },

  async hideManualCombineResultCard({ commit, dispatch }, jobId: string) {
    commit('hideManualCombineResultCard', { jobId });
    await dispatch('deleteJobById', jobId);
  },

  setJob({ commit }, job: Job) {
    commit('setJob', job);
  },

  unsetJob({ commit }) {
    commit('setJob', undefined);
  },

  /**
   * Proxy api service for other modules
   */
  getApiService() {
    return ApiService;
  }
};

const mutations: MutationTree<State> = {
  setVersion(state, payload) {
    state.version = payload;
  },

  setConfig(
    state,
    {
      config,
      constants,
      enums
    }: { config: Config; constants: Record<string, any>; enums: Record<string, any> }
  ) {
    state.config = config;
    // apply fallback for optional theme colors
    state.config.view.theme.brand = config.view.theme.brand ?? config.view.theme.primary;
    state.config.view.theme['on-brand'] = config.view.theme['on-brand'] ?? '#FFFFFF';

    state.constants = constants;
    state.enums = enums;
  },

  setJob(state, job: Job) {
    state.job = job;
    if (job) {
      state.ordersProcessing.hasUserUnfinishedJobs = true;
      state.ordersProcessing.jobsInFrontOfThisOne = job.jobsInFrontOfThisOne;
    }
  },

  setOrdersProcessing(state, payload) {
    state.ordersProcessing = payload;
  },

  addManualCombination(
    state,
    {
      freightIds,
      jobId,
      message
    }: { freightIds: string[] | undefined; jobId: string; message: string }
  ) {
    const newManualCombination: ManualCombinationResult = {
      freightIds: freightIds ? [...freightIds] : undefined,
      jobId: jobId,
      resultCardMessage: message
    };
    state.manualCombinationList.push(newManualCombination);
  },

  hideManualCombineResultCard(state, { jobId }: { jobId: string }) {
    state.manualCombinationList = state.manualCombinationList.filter((mc) => mc.jobId !== jobId);
  },

  removeManualCombinations(state) {
    state.manualCombinationList = [];
  },

  removeManualCombinationByJobId(state, jobId: string) {
    state.manualCombinationList = state.manualCombinationList.filter((mc) => mc.jobId !== jobId);
  },

  setCancelOrdersProcessing(state, payload) {
    state.cancelOrdersProcessing = payload;
  },

  setLoading(state, isLoading) {
    state.isLoading = isLoading;
  },

  storeFilters(
    state,
    payload: {
      viewName: string;
      isOpen: boolean;
      values: Object;
    }
  ) {
    state.filters[payload.viewName] = payload;
  },

  setFetchOrdersProcessingState(
    state,
    { intervalId, timeoutMs }: { intervalId?: NodeJS.Timeout; timeoutMs?: number }
  ) {
    state.checkOrdersProcessing.interval = intervalId;
    state.checkOrdersProcessing.timeoutMs = timeoutMs;
  }
};

const getters: GetterTree<State, RootState> = {
  cancelOrdersProcessing: (state) => state.cancelOrdersProcessing,
  colors: (state) => state.config?.colors,
  configMode: (state) => state.config?.view.mode,
  defaultFilters: (state) => state.config?.defaultFilters,
  fieldEditSettings: (state) => state.config?.fieldEditSettings,
  filters: (state) => state.filters,
  hasUserUnfinishedJobs: (state) => state.ordersProcessing.hasUserUnfinishedJobs,
  isLoading: (state) => state.isLoading,
  jobId: (state) => state.job?.jobId,
  logisticsPlannerType: (state) => state.config?.logisticsPlannerType,
  manualCombineResultCardList: (state) => state.manualCombinationList,
  mapPlannerFilterPanelActive: (state) => state.config?.mapPlannerOptions?.filter,
  mapPlannerPopupOptions: (state) => state.config?.mapPlannerOptions?.popup,
  mapPlannerNotSelectableStatusColors: (state) =>
    state.config?.mapPlannerOptions?.mapColors?.notSelectable,
  mapPlannerSelectableStatusColors: (state) =>
    state.config?.mapPlannerOptions?.mapColors?.selectable,
  mapPlannerStatusColors: (state) => {
    return {
      ...state.config?.mapPlannerOptions?.mapColors?.selectable,
      ...state.config?.mapPlannerOptions?.mapColors?.notSelectable
    };
  },
  mapPlannerSummaryOptions: (state) => state.config?.mapPlannerOptions?.summary,
  ordersProcessing: (state) => state.ordersProcessing,
  palletTypes: (state) => state.config?.palletTypes,
  productCategory: (state) => state.config?.productCategory ?? [],
  testOrdersJson: (state) => state.config?.testOrdersJson,
  viewConfig: (state) => state.config?.view,
  orderEntitiesConfig: (state) => state.config?.entities?.orders ?? [],
  freightEntitiesConfig: (state) => state.config?.entities?.freights ?? [],
  pickupEntitiesConfig: (state) => state.config?.entities?.pickups ?? []
};

export const app = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
